import { getMeatById } from '../db';

/**
 * Get the display name for a meat, including variant if applicable
 * @param {string} meatId - The ID of the meat
 * @param {string} variantId - The variant ID (optional)
 * @returns {string} The formatted meat display name
 */
export const getMeatDisplayName = (meatId, variantId) => {
  if (!meatId) return '';
  
  const meat = getMeatById(parseInt(meatId)); // Ensure meatId is an integer for lookup
  if (!meat) return '';
  
  let meatName = meat.name;
  
  if (variantId) {
    // Convert variantId to number for consistent comparison, as it might come as a string
    const numericVariantId = parseInt(variantId);

    // Check if the meat is Corned Beef (ID 1) or Pastrami (ID 2)
    if (meat.id === 1 || meat.id === 2) {
      const variant = meat.variants?.find(v => v.id === numericVariantId);
      // Prepend variant name (e.g., "Lean", "Fatty") if it's not "Regular"
      if (variant && variant.name !== "Regular") {
        return `${variant.name} ${meatName}`;
      }
      // If it's "Regular" or no specific variant found, just return the meat name
      // This also covers cases where a variant ID might be passed but isn't meaningful (e.g. for "Regular")
    } else if (meat.id === 13) { // Salami ID
      // For salami, use the variant name directly (e.g., "Soft Salami")
      const variant = meat.variants?.find(v => v.id === numericVariantId);
      if (variant) return variant.name;
    } else if (meat.id === 21 || meat.id === 18) { // Hot Dog ID (21) or Beef Tongue ID (18)
      const variant = meat.variants?.find(v => v.id === numericVariantId);
      if (variant) {
        // Prepend variant name, e.g., "1/4 lb Hot Dog" or "Lean Beef Tongue"
        // For "Regular" variants, we still want to show "Regular Beef Tongue" etc.
        // unlike Corned Beef/Pastrami where "Regular" is omitted.
        return `${variant.name} ${meatName}`;
      }
    }
    // For other meats with variants, if any, this logic might need expansion.
    // For now, if it's not CB/Pastrami/Salami/HotDog/BeefTongue, and has a variantId,
    // we'll just return the base meat name.
  }
  
  return meatName;
};

/**
 * Determines if a meat combo is a special priced combo
 * @param {string|number} primaryMeatId - The ID of the primary meat
 * @param {string|number} secondaryMeatId - The ID of the secondary meat
 * @returns {boolean} True if this is a special priced combo
 */
export const isSpecialPricedCombo = (primaryMeatId, secondaryMeatId) => {
  if (!primaryMeatId || !secondaryMeatId) return false;
  
  // Handle numeric IDs (from database) - 1 is Corned Beef, 11 is Chop Liver
  if (typeof primaryMeatId === 'number' || !isNaN(parseInt(primaryMeatId))) {
    const primaryId = parseInt(primaryMeatId);
    const secondaryId = parseInt(secondaryMeatId);
    
    return (primaryId === 1 && secondaryId === 11) || 
           (primaryId === 11 && secondaryId === 1);
  }
  
  // Handle string IDs with variants
  if (typeof primaryMeatId === 'string' && primaryMeatId.includes('-')) {
    const primaryBaseMeat = primaryMeatId.split('-')[0];
    const secondaryBaseMeat = secondaryMeatId.split('-')[0];
    
    // Check for Corned Beef & Chop Liver combo
    return (primaryBaseMeat === 'corned-beef' && secondaryBaseMeat === 'chop-liver') || 
           (primaryBaseMeat === 'chop-liver' && secondaryBaseMeat === 'corned-beef');
  }
  
  return false;
};

/**
 * Determines if a meat combo is the popular corned beef & pastrami combo
 * @param {string|number} primaryMeatId - The ID of the primary meat
 * @param {string|number} secondaryMeatId - The ID of the secondary meat
 * @returns {boolean} True if this is the popular combo
 */
export const isPopularCombo = (primaryMeatId, secondaryMeatId) => {
  if (!primaryMeatId || !secondaryMeatId) return false;
  
  // Handle numeric IDs (from database) - 1 is Corned Beef, 2 is Pastrami
  if (typeof primaryMeatId === 'number' || !isNaN(parseInt(primaryMeatId))) {
    const primaryId = parseInt(primaryMeatId);
    const secondaryId = parseInt(secondaryMeatId);
    
    return (primaryId === 1 && secondaryId === 2) || 
           (primaryId === 2 && secondaryId === 1);
  }
  
  // Handle string IDs with variants
  if (typeof primaryMeatId === 'string' && primaryMeatId.includes('-')) {
    const primaryBaseMeat = primaryMeatId.split('-')[0];
    const secondaryBaseMeat = secondaryMeatId.split('-')[0];
    
    return (primaryBaseMeat === 'corned-beef' && secondaryBaseMeat === 'pastrami') || 
           (primaryBaseMeat === 'pastrami' && secondaryBaseMeat === 'corned-beef');
  }
  
  return false;
};